import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

// Full-width bar (llc-automation styling): bg-surface, gold hairline border, h-16,
// content capped at max-w-7xl. DAG wordmark + Canon on the left, a Business Areas
// dropdown that switches the view, theme toggle on the right.

interface NavbarProps {
  areas: string[];
  area: string;
  onArea: (a: string) => void;
}

const ChevronDown: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox='0 0 20 20'
    fill='none'
    stroke='currentColor'
    strokeWidth={1.6}
    aria-hidden
  >
    <path d='M6 8l4 4 4-4' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

// Auth-aware account menu: a round initial that opens email + sign-out. Renders
// nothing until the session is authenticated.
const UserMenu: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth0();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const onClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
  }, []);
  useEffect(() => {
    if (!open) return;
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open, onClickOutside]);

  if (!isAuthenticated) return null;
  const email = user?.email ?? '';
  const initial = (user?.name || email || '?').charAt(0).toUpperCase();

  return (
    <div className='relative' ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className='flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary'
        style={{ background: 'var(--color-primary)' }}
        aria-haspopup='menu'
        aria-expanded={open}
        title={email}
      >
        {initial}
      </button>
      {open && (
        <div className='absolute right-0 mt-1.5 w-56 rounded-xl border border-theme-border bg-surface p-1.5 shadow-2xl ring-1 ring-black/5'>
          <div className='truncate px-3 py-2 text-xs text-on-surface-tertiary' title={email}>
            {email}
          </div>
          <div className='my-1 border-t border-theme-border' />
          <button
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className='block w-full rounded-lg px-3 py-2 text-left text-sm text-[color:var(--color-danger)] transition-colors hover:bg-surface-tertiary'
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export const Navbar: React.FC<NavbarProps> = ({ areas, area, onArea }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const onClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open, onClickOutside]);

  return (
    <nav className='sticky top-0 z-40 border-b border-hairline bg-surface/95 shadow-sm backdrop-blur'>
      <div className='mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8'>
        <div className='flex items-center gap-5'>
          <button onClick={() => onArea('Revenue')} className='flex items-center gap-2.5'>
            <Logo className='h-6 w-auto text-on-surface' />
            <span className='h-5 w-px bg-theme-border' aria-hidden />
            <span
              className='text-lg font-semibold tracking-tight text-on-surface'
              style={{ fontFamily: "'Spectral', Georgia, serif" }}
            >
              Canon
            </span>
          </button>

          {/* Business Areas dropdown */}
          <div className='relative' ref={ref}>
            <button
              onClick={() => setOpen((v) => !v)}
              className='flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-on-surface transition-colors hover:bg-surface-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary'
              aria-haspopup='menu'
              aria-expanded={open}
            >
              {area}
              <ChevronDown
                className={'h-4 w-4 transition-transform ' + (open ? 'rotate-180' : '')}
              />
            </button>
            {open && (
              <div className='absolute left-0 mt-1.5 w-60 rounded-xl border border-theme-border bg-surface p-1.5 shadow-2xl ring-1 ring-black/5'>
                <div className='px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-on-surface-quaternary'>
                  Business Areas
                </div>
                {areas.map((a) => (
                  <button
                    key={a}
                    onClick={() => {
                      onArea(a);
                      setOpen(false);
                    }}
                    className={
                      'block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ' +
                      (a === area
                        ? 'bg-surface-tertiary font-medium text-on-surface'
                        : 'text-on-surface-secondary hover:bg-surface-tertiary hover:text-on-surface')
                    }
                  >
                    {a}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className='flex items-center gap-4'>
          <ThemeToggle />
          <UserMenu />
        </div>
      </div>
    </nav>
  );
};
